/**
 * Authentication and Authorization Types
 *
 * This module defines the TypeScript types for the hybrid Clerk + Application-level
 * RBAC system as outlined in the role-based security implementation plan.
 */

// Clerk Organization Roles (Default - No Manual Setup Required)
export type ClerkRole = "org:admin" | "org:member";

// Application-Level Role Granularity
export type AppRole = "owner" | "admin" | "manager" | "member";

// Core Permissions (Application-Level)
export type AppPermission =
  // Business Operations
  | "leads:read"
  | "leads:write"
  | "leads:delete"
  | "clients:read"
  | "clients:read_all"
  | "locations:read"
  | "clients:onboarding"
  | "clients:write"
  | "clients:delete"
  | "bids:read"
  | "bids:write"
  | "bids:delete"
  | "contracts:read"
  | "contracts:write"
  | "contracts:delete"

  // Subcontracting Platform & Features
  | "opportunities:read"
  | "opportunities:write"
  | "applications:read"
  | "applications:review"
  | "applications:manage"
  | "documents:read"
  | "documents:write"
  | "documents:grant_access"
  | "automation:read"
  | "knowledge_base:read"
  | "reviews:read"

  // Team & Organization
  | "team:read"
  | "team:write"
  | "team:invite"
  | "team:remove"
  | "organization:read"
  | "organization:write"
  | "organization:billing"
  | "analytics:read"
  | "analytics:advanced"

  // System Administration
  | "settings:read"
  | "settings:write"
  | "audit:read"

  // Special permission for owners (grants all permissions)
  | "*";

// User Role Context Interface
export interface UserRoleContext {
  // Clerk data
  clerkRole: ClerkRole;

  // Application data
  appRole: AppRole;
  permissions: AppPermission[];
  isOwner: boolean;

  // Additional context
  userId: string;
  organizationId?: string;
  roleFlags?: Record<string, boolean>;
}

// Role-Permission Mapping
export const ROLE_PERMISSIONS: Record<AppRole, AppPermission[]> = {
  owner: ["*"], // All permissions
  admin: [
    "leads:read",
    "leads:write",
    "leads:delete",
    "clients:read",
    "clients:read_all",
    "locations:read",
    "clients:onboarding",
    "clients:write",
    "clients:delete",
    "bids:read",
    "bids:write",
    "bids:delete",
    "contracts:read",
    "contracts:write",
    "contracts:delete",
    "opportunities:read",
    "opportunities:write",
    "applications:read",
    "applications:review",
    "applications:manage",
    "documents:read",
    "documents:write",
    "documents:grant_access",
    "automation:read",
    "knowledge_base:read",
    "reviews:read",
    "team:read",
    "team:write",
    "team:invite",
    "team:remove",
    "organization:read",
    "organization:write",
    "analytics:read",
    "analytics:advanced",
    "settings:read",
    "settings:write",
    "audit:read",
  ],
  manager: [
    "leads:read",
    "leads:write",
    "clients:read",
    "clients:read_all",
    "locations:read",
    "clients:onboarding",
    "clients:write",
    "bids:read",
    "bids:write",
    "contracts:read",
    "contracts:write",
    "opportunities:read",
    "opportunities:write",
    "applications:read",
    "applications:review",
    "documents:read",
    "documents:write",
    "reviews:read",
    "team:read",
    "team:invite",
    "organization:read",
    "analytics:read",
  ],
  member: [
    "locations:read",
    "clients:read",
    "documents:read",
    "team:read",
    "organization:read",
    "analytics:read",
  ],
};

// Clerk Role to Application Role Mapping Helper
export const CLERK_TO_APP_ROLE_MAPPING: Record<ClerkRole, AppRole[]> = {
  "org:admin": ["owner", "admin"],
  "org:member": ["manager", "member"],
};

// Route Permission Mapping
export const ROUTE_PERMISSIONS: Record<string, AppPermission[]> = {
  // Admin and Settings
  "/admin": ["settings:write"],
  "/settings": ["settings:read"],
  "/settings/organization": ["organization:write"],
  "/settings/billing": ["organization:billing"],
  "/audit": ["audit:read"],

  // Analytics
  "/analytics": ["analytics:read"],
  "/analytics/advanced": ["analytics:advanced"],

  // Team Management
  "/team": ["team:read"],
  "/team/invite": ["team:invite"],
  "/team/manage": ["team:write"],
  "/employees": ["team:read"],

  // Business Operations
  "/leads": ["leads:read"],
  "/clients": ["clients:read_all"],
  "/clients/locations": ["locations:read"],
  "/clients/onboarding": ["clients:onboarding"],
  "/bids": ["bids:read"],
  "/automation-hub": ["automation:read"],
  "/knowledge-base": ["knowledge_base:read"],
  "/reviews": ["reviews:read"],

  // Documents
  "/documents/vault": ["documents:read"],
  "/documents/grants": ["documents:grant_access"],
};

// Permission Categories for UI Organization
export const PERMISSION_CATEGORIES = {
  business: {
    label: "Business Operations",
    permissions: [
      "leads:read",
      "leads:write",
      "leads:delete",
      "clients:read",
      "clients:read_all",
      "locations:read",
      "clients:onboarding",
      "clients:write",
      "clients:delete",
      "bids:read",
      "bids:write",
      "bids:delete",
      "contracts:read",
      "contracts:write",
      "contracts:delete",
    ] as AppPermission[],
  },
  subcontracting: {
    label: "Subcontracting Platform",
    permissions: [
      "opportunities:read",
      "opportunities:write",
      "applications:read",
      "applications:review",
      "applications:manage",
      "documents:read",
      "documents:write",
      "documents:grant_access",
      "automation:read",
      "knowledge_base:read",
      "reviews:read",
    ] as AppPermission[],
  },
  team: {
    label: "Team & Organization",
    permissions: [
      "team:read",
      "team:write",
      "team:invite",
      "team:remove",
      "organization:read",
      "organization:write",
      "organization:billing",
      "analytics:read",
      "analytics:advanced",
    ] as AppPermission[],
  },
  system: {
    label: "System Administration",
    permissions: ["settings:read", "settings:write", "audit:read"] as AppPermission[],
  },
} as const;

// Type guards for runtime type checking
export function isClerkRole(role: string): role is ClerkRole {
  return role === "org:admin" || role === "org:member";
}

export function isAppRole(role: string): role is AppRole {
  return ["owner", "admin", "manager", "member"].includes(role);
}

export function isAppPermission(permission: string): permission is AppPermission {
  const allPermissions = Object.values(ROLE_PERMISSIONS).flat();
  return allPermissions.includes(permission as AppPermission) || permission === "*";
}

// Helper type for permission checking functions
export type PermissionChecker = (permission: AppPermission) => boolean;

// Error types for authorization failures
export class AuthorizationError extends Error {
  constructor(
    message: string,
    public readonly requiredPermission: AppPermission,
    public readonly userPermissions: AppPermission[]
  ) {
    super(message);
    this.name = "AuthorizationError";
  }
}

export class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthenticationError";
  }
}
